import React, { useState } from 'react';
import dynamic from 'next/dynamic';
import { useModal } from '@blueprint/ui';

import { Page, HomeValueAndPropertySearchWrapper } from './Homepage.styles';
import { AuthModalMode } from 'shared/components/AuthModal/AuthModal.types';
import { getTranslator } from '../../shared/i18n/getTranslator';
import homepageTranslations from 'public/locales/en/homepage.json';

import {
    HomepageHead,
    HeroContainer,
    HowMuchIsMyHomeWorth,
    RegistrationAndLogin,
    SearchSuggestionsTownsAndCities,
    PropertyNews
} from '../components';
import { Tab } from '../components/HeroContainer/HeroContainer.types';
import { ContentfulImageWrapper } from 'homepage/queries/heroContent.types';
import { ArticlesForHomepageQuery } from 'homepage/queries/propertyNews.generated';
import { HomepageRegistrationFooter } from 'homepage/components/HomepageRegistrationFooter/HomepageRegistrationFooter';
import { useIsLoggedIn } from 'shared/hooks/useIsLoggedIn';
import usePageTracking from 'shared/hooks/usePageTracking';
import { TaxonomyPage, TaxonomySection } from 'shared/tracking/contracts';

const t = getTranslator(homepageTranslations);

type HomepageProps = {
    brandHeadline: string;
    h1TitleTag: string;
    heroImage: ContentfulImageWrapper;
    propertyNewsArticles: ArticlesForHomepageQuery['articles'];
};

const DynamicAuthModal = dynamic(
    () => import('shared/components/AuthModal/AuthModal'),
    { ssr: false }
);

export const Homepage = (props: HomepageProps) => {
    const {
        brandHeadline,
        h1TitleTag,
        heroImage,
        propertyNewsArticles
    } = props;
    const [isAuthModalShowing, openAuthModal, closeAuthModal] = useModal();
    const [tabSelected, setTabSelected] = useState<Tab>('for-sale');
    const [authMode, setAuthMode] = useState<AuthModalMode>('sign-in');

    usePageTracking(TaxonomyPage.HOMEPAGE, {
        section: TaxonomySection.HOMEPAGE
    });

    const handleOpenAuthModal = (
        triggerOrigin: React.RefObject<HTMLElement> | null,
        mode: AuthModalMode
    ) => {
        setAuthMode(mode);
        openAuthModal(triggerOrigin);
    };

    const isLoggedIn = useIsLoggedIn();

    return (
        <>
            <HomepageHead />
            <Page>
                <HeroContainer
                    tabSelected={tabSelected}
                    setTabSelected={setTabSelected}
                    brandHeadline={brandHeadline}
                    title={h1TitleTag}
                    image={heroImage}
                    mainContentId="main-content"
                />
                <HomeValueAndPropertySearchWrapper>
                    <HowMuchIsMyHomeWorth isLoggedIn={isLoggedIn} />
                    {isLoggedIn ? null : (
                        <RegistrationAndLogin
                            openAuthModal={handleOpenAuthModal}
                        />
                    )}
                </HomeValueAndPropertySearchWrapper>
                <SearchSuggestionsTownsAndCities />
                <PropertyNews articles={propertyNewsArticles} />
                <HomepageRegistrationFooter
                    openAuthModal={handleOpenAuthModal}
                />
            </Page>
            <DynamicAuthModal
                heading={t('auth_modal.heading')}
                initialMode={authMode}
                isShowing={isAuthModalShowing}
                close={closeAuthModal}
                actionAfterAuth={{
                    action: () => null,
                    eventLabel: 'homepage'
                }}
            />
        </>
    );
};

export default Homepage;
